import { assert, ITimeSource, logger } from '@fe/core';
import { FeMap } from '@fe/maplibre-extensions';
import {
    getQualifiedId,
    SelectedWeatherItemType,
    WeatherLayerPlugin,
    WeatherProductFileInfo,
    WeatherSettingsChangeSource
} from '@fe/weather';
import { store } from '../app/state/store';
import { UdViewModel, UDViewSUAModel } from '../udView/udview';
import { weatherSettingsFromUdView } from '../udView/weather-settings-from-view';

const DSUA_ID = 122;

export class FeWeatherPlugin extends WeatherLayerPlugin {
    private view: UdViewModel | null = null;
    private authorizedProducts = new Map<number, WeatherProductFileInfo>();

    constructor(workspaceId: number, time: ITimeSource) {
        super(workspaceId, time);
    }

    onAdd(map: FeMap): void {
        super.onAdd(map);
        this.loadProductsFromView();
    }

    productsUpdated(products: WeatherProductFileInfo[], options?: { force: boolean }) {
        // Make this common for all maps
        this.authorizedProducts = new Map<number, WeatherProductFileInfo>(products.map((x) => [x.id, x]));

        if (options?.force) {
            this.refreshProducts({ force: true });
        } else {
            // Check if we have any of the updated products loaded
            if (this.products.some((layer) => products.some((p) => p.id === layer.id))) {
                this.refreshProducts();
            }
        }
    }

    getProductLoadInfo(id: number): WeatherProductFileInfo | null {
        return this.authorizedProducts.get(id) ?? null;
    }

    productIsAuthorized(id: number): boolean {
        // TODO: make this better
        const state = store.getState();

        if (state.global.playback.active) {
            // In playback any recorded weather is authorized
            return true;
        }

        return store.getState().weather.authorized.includes(id);
    }

    setView(view: UdViewModel) {
        this.view = view;
        this.loadProductsFromView();
    }

    getUdViewList() {
        return {
            all: this.products
                .filter((x) => x.type === SelectedWeatherItemType.weather && x.id !== DSUA_ID)
                .map((x) => getQualifiedId(x.product)),
            hidden: this.products
                .filter((x) => !x.visible && x.type === SelectedWeatherItemType.weather && x.id !== DSUA_ID)
                .map((x) => getQualifiedId(x.product))
        };
    }

    onSuaSettingsChanged(sua: UDViewSUAModel) {
        assert(this.settings);

        const previousShow = this.settings.dsua.show;
        const newShow = sua.show && sua.dynamic;
        if (newShow && !previousShow) {
            this.addProduct(DSUA_ID, SelectedWeatherItemType.static);
        } else if (!newShow && previousShow) {
            this.removeProduct(DSUA_ID, SelectedWeatherItemType.static);
        }

        this.updateSettings((settings) => {
            // Copy sua settings over, don't use the original structure for compatibility with web API
            settings.dsua.show = newShow;
            settings.dsua.alert = sua.alert;
            settings.dsua.caution = sua.caution;
            settings.dsua.danger = sua.danger;
            settings.dsua.militaryArea = sua.militaryArea;
            settings.dsua.prohibited = sua.prohibited;
            settings.dsua.restricted = sua.restricted;
            settings.dsua.training = sua.traning;
            settings.dsua.warning = sua.warning;
            settings.dsua.unknown = sua.unkown;
            settings.dsua.filterGroup = sua.filterGroup;
            // Min level is part of the weather settings list
        }, WeatherSettingsChangeSource.dsua);
    }

    private loadProductsFromView() {
        if (!this.view) {
            return;
        }

        assert(this.mapLayerStack);
        logger.debug("Loading view '%s'", this.view.name);

        this.replaceProducts(
            {
                weather: this.view.weather.all,
                hidden: this.view.weather.hidden,
                weatherVisibility: this.view.weather.show,
                staticProducts: this.view.staticData.sua.show && this.view.staticData.sua.dynamic ? [DSUA_ID] : []
            },
            weatherSettingsFromUdView(this.view)
        );
    }
}
